import { apiVersionMap, type APIKeys } from '../../api/version';
import { getSystemParam } from '../platform';
import { ERROR_CODE } from '../../error/code';
import { createError } from '../../error/hybrid-error';

export type APIAvailableCache = { [key: string]: boolean };
export type Platforms = 'mac' | 'windows' | 'linux' | 'ios' | 'android';
export type ShortPlatforms = 'm' | 'w' | 'l' | 'i' | 'a';

const PLATFORM_MAP: Record<Platforms, ShortPlatforms> = {
  mac: 'm',
  windows: 'w',
  linux: 'l',
  ios: 'i',
  android: 'a',
};
const getKeyByValue = (value: ShortPlatforms): Platforms => {
  return Object.keys(PLATFORM_MAP).find(
    (key) => PLATFORM_MAP[key as Platforms] === value
  )! as Platforms;
};

const apiAvailableCache: APIAvailableCache = {};
function isAvailable(apiName: APIKeys) {
  return new Promise((resolve, reject) => {
    if (Object.prototype.hasOwnProperty.call(apiAvailableCache, apiName)) {
      if (apiAvailableCache[apiName]) {
        resolve(true);
      } else {
        reject(`${apiName} is not available in current client!`);
      }
    } else {
      getSystemParam()
        .then((systemParam) => {
          const { clientVersion, deviceOs } = systemParam;
          const platform = deviceOs.toLowerCase() as Platforms;
          const shortPlatform = PLATFORM_MAP[platform];
          const apiAvailableState: { [key in APIKeys]?: boolean } = {};
          for (const key in apiVersionMap) {
            const api = key as APIKeys;

            const supportVersion = apiVersionMap[api][shortPlatform] as
              | string
              | undefined;
            if (supportVersion) {
              apiAvailableState[api] = isVersionSupported(
                clientVersion,
                supportVersion
              );
            } else {
              apiAvailableState[api] = false;
            }
          }
          if (apiAvailableState[apiName]) {
            apiAvailableCache[apiName] = true;
            resolve(true);
          } else {
            apiAvailableCache[apiName] = false;
            if (apiVersionMap[apiName]) {
              if (apiVersionMap[apiName]?.[shortPlatform]) {
                reject(
                  `The current client version(${clientVersion}) does not meet the minimum version requirement for the API(${apiName}). Please upgrade the client to:` +
                    JSON.stringify(apiVersionMap[apiName]?.[shortPlatform])
                );
              } else {
                const supportedInfo = Object.entries(
                  apiVersionMap[apiName]
                ).reduce<Record<Platforms, string>>(
                  (acc, [shortPlatform, version]) => {
                    const platform = getKeyByValue(
                      shortPlatform as ShortPlatforms
                    );
                    if (platform) {
                      acc[platform] = version;
                    }
                    return acc;
                  },
                  {} as Record<Platforms, string>
                );
                reject(
                  `The current platform(${platform}) does not support this API. supported platform: ${JSON.stringify(supportedInfo)}`
                );
              }
            } else {
              reject(
                `No compatibility information was found for this API(${apiName}). Below are all the valid API: ` +
                  Object.keys(apiVersionMap).join(',')
              );
            }
          }
        })
        .catch((e) => {
          console.warn(
            'This client not support isAvailable api, so default allow api call.',
            e
          );
          apiAvailableCache[apiName] = true;
          resolve(true);
        });
    }
  });
}

function isVersionSupported(currentVersion: string, apiVersion: string) {
  // for the better dev experience, skip version check for local build
  if (currentVersion === 'ZoomBundleVersionNumber') {
    console.warn(
      '[hybrid/jssdk] Skip version check for local development build! 🚀'
    );
    return true;
  }
  return notLess(currentVersion, apiVersion);
}

function notLess(currentVersion: string, targetVersion: string) {
  return compareSemVer(currentVersion, targetVersion) >= 0;
}

/**
 * version1 < version2 => -1
 * version1 = version2 =>  0
 * version1 > version2 =>  1
 * @param version1
 * @param version2
 * @return {number}
 */
function compareSemVer(version1: string, version2: string) {
  const splitVersion = (version: string) => version.split('.').map(Number);

  const v1 = splitVersion(version1);
  const v2 = splitVersion(version2);

  for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
    const num1 = v1[i] || 0;
    const num2 = v2[i] || 0;

    if (num1 < num2) {
      return -1;
    } else if (num1 > num2) {
      return 1;
    }
  }

  return 0;
}

function genErrorMessage({
  errorCode,
  apiName,
  message,
}: {
  errorCode: number;
  apiName: string;
  message: string;
}) {
  return `[${apiName}] Error(${errorCode}): ${message}`;
}

function isInUnifyWebview() {
  return !!(window.unifyConfig || window.android_config);
}

export function genRandomId(prefix = 'id') {
  return prefix + Math.random().toString(16).slice(2);
}

export {
  isAvailable,
  genErrorMessage,
  isInUnifyWebview,
  ERROR_CODE,
  createError,
  compareSemVer,
};
